import { NodeRequestConfig } from '../../../../ui/gzTree/uiElements/gzNodeRenameInput/helpers/types';
import { TreeNodeData } from '../../types';
import { ConsoleRequest, DataProviderType } from '../../../../utils/request';
import { BaseActionHandlers } from './base.action.handlers';
import { AsyncValidator, Validation, ValidatorType } from '../../../../ui/gzTree/type/validators';
import { throttleRequest } from '../../../../ui/gzTree/__mocks';
import { translateFn } from '../../../../utils/translator.helper';
import { GzToast, ToastSeverity } from '../../../../ui/gzToastNotification/type';
import { GzToastNotificationWebComponent } from '../../../../ui/gzToastNotification';

export class CreateCompanyFolderActionHandlers extends BaseActionHandlers {
  validator = {
    // the base class only looks for the RENAME validator
    [ValidatorType.RENAME]: [new CreateCompanyFolderAsyncValidator()],
  };

  protected async _createNodeRequest(nodeRequest: NodeRequestConfig): Promise<TreeNodeData> {
    nodeRequest.nodeData.originalData.name = nodeRequest.changes.name as string;
    nodeRequest.nodeData.originalData.parentId = nodeRequest.nodeData.parentNodeId;

    try {
      const response = await ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.EPS,
        endpoint: 'Company',
        method: 'createCompanyFolders',
        payload: {
          records: [nodeRequest.nodeData.originalData],
        },
      });

      const toastNotificationConfigs: GzToast = {
        message: translateFn('Company folder successfully created.'),
        hasAction: false,
        severity: ToastSeverity.SUCCESS,
      };
      GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs);

      nodeRequest.nodeData.originalData = response.data[0];
      nodeRequest.nodeData.nodeName = nodeRequest.nodeData.originalData.name;
      nodeRequest.nodeData.nodeId = nodeRequest.nodeData.originalData._id;
      return nodeRequest.nodeData;
    } catch (exception) {
      const isDuplicateError = exception.err.message.includes('Name already exists in group');
      const message = isDuplicateError
        ? translateFn('Duplicate entry found. Unable to save changes.')
        : translateFn('An error has occurred. Please try again later.');
      const toastErrorConfig: GzToast = {
        message,
        hasAction: false,
        severity: ToastSeverity.ERROR,
      };
      GzToastNotificationWebComponent.buildToastWrapperElement(toastErrorConfig);
      throw new Error('Failed to create folder');
    }
  }
}

export class CreateCompanyFolderAsyncValidator extends AsyncValidator {
  public async validate(node: TreeNodeData, additionalData?: string): Promise<Validation> {
    if (!additionalData || additionalData.trim() === '') {
      return {
        validation: translateFn('This field is required.'),
      };
    }

    const response = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.EPS,
      endpoint: 'Company',
      method: 'checkIfCompanyFolderNameIsUnique',
      payload: {
        id: node.nodeId,
        name: additionalData,
        parentId: node.parentNodeId,
      },
    });

    /* In case of error, we need to return an object with a validation key because that's what's expected in
      GzNodeRenameInputWebComponent.ts, for the errorTooltipText property */
    return response.success
      ? throttleRequest(null)
      : {
          validation: translateFn('The name already exists in this folder.'),
        };
  }
}
